export enum Features {
  FEATURE_DIRECT_BOOKING = "FEATURE_DIRECT_BOOKING",
  FEATURE_FUND_REQUEST = "FEATURE_FUND_REQUEST",
  FEATURE_DIRECT_BOOKING_HISTORY = "FEATURE_DIRECT_BOOKING_HISTORY",
  FEATURE_AFFILIATE_DASHBOARD = "FEATURE_AFFILIATE_DASHBOARD",
  FEATURE_ACTIVATION_CODE = "FEATURE_ACTIVATION_CODE",
}

export enum FilterTrip {
  FILTER_FUTURE_TRIP = "Future-Trips",
  FILTER_PAST_TRIP = "Past-Trips",
  FILTER_ONEWAY_TRIP = "One-Way",
  FILTER_ROUND_TRIP = "Round-Trip",
}

export const baggagePiecesOption = [
  { value: 0, label: "Number of pieces" },
  { value: 1, label: "1 piece" },
  { value: 2, label: "2 pieces" },
  { value: 3, label: "3 pieces" },
  { value: 4, label: "4 pieces" },
];

export enum DEFAULT {
  AIRLINE_LOGO = "https://t4.ftcdn.net/jpg/02/51/95/53/360_F_251955356_FAQH0U1y1TZw3ZcdPGybwUkH90a3VAhb.jpg",
}

export const statuses = [
  {
    label: "Draft",
    value: "draft",
  },
  {
    label: "Published",
    value: "published",
  },
];

export enum VisaStatus {
  PENDING = "VISA_PENDING",
  GRANTED = "VISA_GRANTED",
  DENIED = "VISA_DENIED",
}

/**
 * Client Inquiry Link (flight quotations / booking inquiries) gate — the Create
 * Quote Link button on /flight-booking, the /flight-quotations list and the
 * Booking Inquiries page (menu entries and the pages themselves) all share
 * this, so they can never drift apart.
 *
 * As of the Sep 25 2026 pricing update this is a Business-plan perk, replacing
 * the earlier pilot account list. `subscriptions.id` 2 is Business — same
 * convention as the subscription_id checks in BusinessSetting.tsx and
 * BuyActivationForm.tsx. The API enforces the same rule in
 * FlightQuotationController::BUSINESS_SUBSCRIPTION_ID — lift both together to
 * open the feature to every plan.
 *
 * Whitelabel: the whitelabel admin always has it; agents under a whitelabel
 * admin never do (same rule as User::canUseFlightQuotation on the API).
 */
export const BUSINESS_SUBSCRIPTION_ID = 2;

export const canUseFlightQuotation = (currentUser: any): boolean => {
  if (currentUser?.is_whitelabel_admin) return true;
  if (currentUser?.is_whitelabel) return false;
  return (
    Number(currentUser?.company?.subscription_id) === BUSINESS_SUBSCRIPTION_ID
  );
};

/**
 * Rebranding services (the sidebar's Rebranding group) are excluded from the
 * Standard package for accounts registered from 2026-10-15. The API decides
 * (User::canUseRebranding, flag `can_use_rebranding`) and also blocks the
 * endpoints via the `rebranding` middleware.
 */
export const canUseRebranding = (currentUser: any): boolean =>
  currentUser?.can_use_rebranding !== false;

/**
 * Whitelabel accounts (see api/app/Support/Whitelabel.php): a whitelabel
 * admin plus the agents it created. They get a trimmed menu and trimmed pages,
 * and no affiliate/referral income. The API sends both flags on the user.
 */
export const isWhitelabel = (currentUser: any): boolean =>
  Boolean(currentUser?.is_whitelabel);

export const isWhitelabelAdmin = (currentUser: any): boolean =>
  Boolean(currentUser?.is_whitelabel_admin);

// The stock Wanderlinx itinerary banner every company gets by default
// (company.itinerary_banner's column default / Constant::DEFAULT_ITINERARY_BANNER).
export const DEFAULT_ITINERARY_BANNER =
  "https://cdn.mywanderlinx.com/assets/images/E-TICKER-BANNER.png";

// True for the stock banner however its URL was stored (http/https, host,
// letter case) — matched on the file name.
export const isDefaultItineraryBanner = (url?: string | null): boolean =>
  /\/e-ticker-banner\.png(\?.*)?$/i.test(String(url || "").trim());

export const WANDERLINX_COVER_IMAGE =
  "https://cdn.mywanderlinx.com/assets/images/wanderlinx.png";

/**
 * The company cover image, or a default when none is uploaded. Whitelabel
 * admins and agents get a brand-neutral default instead of the Wanderlinx one.
 */
export const coverImageSrc = (
  currentUser: any,
  fallback: string = WANDERLINX_COVER_IMAGE
): string =>
  currentUser?.company?.cover_logo ??
  (isWhitelabel(currentUser)
    ? "/assets/images/book-demo/landing-hero-bg.jpg"
    : fallback);

/**
 * Where to send a signed-out visitor. Every *.wltravel.ph tenant subdomain
 * proxies straight to this app (see add-wltravel-tenant.sh on the nginx
 * host), so a hardcoded cross-domain REACT_APP_SIGNIN_URL would always
 * bounce a tenant's visitors off their own custom domain and onto the
 * shared one. This app has its own native /login page (see Login.tsx —
 * calls /api/auth/login directly, no cross-app hash handoff needed), so
 * those hosts go there and stay on their own domain throughout. Every other
 * host (e.g. dashboard.mywanderlinx.com) keeps the original shared signin
 * page, unchanged.
 */
// Tenants' own custom domains, served by the same nginx tenant snippet.
const TENANT_CUSTOM_DOMAINS = ["tattusb2b.com"];

export const getSignInUrl = (): string => {
  const { protocol, hostname } = window.location;

  if (
    hostname.endsWith(".wltravel.ph") ||
    TENANT_CUSTOM_DOMAINS.some(
      (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
    )
  ) {
    return `${protocol}//${hostname}/login`;
  }

  return `${process.env.REACT_APP_SIGNIN_URL}`;
};
